import { useEffect, useState, useMemo } from 'react';
import { collection, onSnapshot, doc, deleteDoc } from 'firebase/firestore';
import {
  Box, Typography, Button, Paper, Chip, Stack, ToggleButton, ToggleButtonGroup
} from '@mui/material';
import { Add } from '@mui/icons-material';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import it from 'date-fns/locale/it';
import { RRule } from 'rrule';
import 'react-big-calendar/lib/css/react-big-calendar.css';

import { db } from './firebase';
import { useAuth } from './AuthContext';
import EventModal from './EventModal';

const locales = { it };
const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: (date) => startOfWeek(date, { weekStartsOn: 1 }),
  getDay,
  locales
});

const messages = {
  allDay: 'Tutto il giorno', previous: 'Precedente', next: 'Successivo',
  today: 'Oggi', month: 'Mese', week: 'Settimana', day: 'Giorno', agenda: 'Agenda',
  date: 'Data', time: 'Ora', event: 'Evento',
  noEventsInRange: 'Nessun evento in questo periodo.',
  showMore: n => `+${n} altri`
};

const OWNER_COLORS = { Mario: '#1976d2', Giuseppina: '#9c27b0' };

function eventStyleGetter(ev) {
  const bg = OWNER_COLORS[ev.owner] || '#607d8b';
  return {
    style: {
      backgroundColor: bg,
      opacity: ev.visibility === 'private' ? 0.7 : 1,
      borderRadius: 4, border: 'none', color: 'white', fontSize: 13
    }
  };
}

const FREQ_MAP = {
  daily:   RRule.DAILY,
  weekly:  RRule.WEEKLY,
  monthly: RRule.MONTHLY
};

// Finestra di espansione: 2 anni prima e dopo oggi
const EXPAND_FROM = new Date(Date.now() - 365 * 2 * 24 * 60 * 60 * 1000);
const EXPAND_TO   = new Date(Date.now() + 365 * 2 * 24 * 60 * 60 * 1000);

function expandEvent(ev) {
  const { ricorrenza, start, end } = ev;
  if (!ricorrenza || !ricorrenza.freq) return [ev];

  const startDate = new Date(start);
  const endDate   = new Date(end);
  const duration  = endDate.getTime() - startDate.getTime();

  const ruleOpts = {
    freq:     FREQ_MAP[ricorrenza.freq] || RRule.WEEKLY,
    interval: ricorrenza.interval || 1,
    dtstart:  startDate
  };
  if (ricorrenza.count) ruleOpts.count = ricorrenza.count;
  if (ricorrenza.until) ruleOpts.until = new Date(ricorrenza.until);

  let dates;
  try {
    dates = new RRule(ruleOpts).between(EXPAND_FROM, EXPAND_TO, true);
  } catch {
    return [ev];
  }

  return dates.map((d, i) => ({
    ...ev,
    // ID univoco per istanza: non tocca il documento Firestore originale
    _instanceId: `${ev.id}_${i}`,
    start: d,
    end:   new Date(d.getTime() + duration),
    title: ev.titolo,
    _isRecurrence: true
  }));
}

export default function Calendario() {
  const { staff } = useAuth();
  const [events, setEvents] = useState([]);
  const [contatti, setContatti] = useState([]);
  const [view, setView] = useState('month');
  const [date, setDate] = useState(new Date());
  const [modal, setModal] = useState(null);

  const isAdmin = staff?.role === 'admin';

  useEffect(() => {
    const u1 = onSnapshot(collection(db, 'crm_eventi'), s => {
      setEvents(s.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    const u2 = onSnapshot(collection(db, 'crm_contatti'), s =>
      setContatti(s.docs.map(d => ({ id: d.id, ...d.data() })))
    );
    return () => { u1(); u2(); };
  }, []);

  // Filtro visibilità
  const visibleEvents = useMemo(() => {
    if (isAdmin) return events;
    return events.filter(e => e.owner === staff?.nome || e.visibility === 'public');
  }, [events, isAdmin, staff]);

  // Espansione ricorrenze → lista flat di istanze per il calendario
  const calendarEvents = useMemo(() =>
    visibleEvents.flatMap(ev => {
      const instances = expandEvent(ev);
      return instances.map(inst => ({
        ...inst,
        start: inst.start instanceof Date ? inst.start : new Date(inst.start),
        end:   inst.end   instanceof Date ? inst.end   : new Date(inst.end),
        title: inst.titolo || inst.title || '(senza titolo)'
      }));
    })
  , [visibleEvents]);

  const handleSelectSlot = ({ start, end }) => {
    setModal({
      titolo: '', start: start.toISOString(), end: end.toISOString(),
      allDay: false, contattoId: null, luogo: '', note: '',
      visibility: 'public', ricorrenza: null
    });
  };

  // Click su evento (anche su istanza ricorrente) → apre il documento originale
  const handleSelectEvent = (ev) => {
    // Recupera evento originale da Firestore (id senza suffisso _N)
    const origId = ev.id;
    const orig = events.find(e => e.id === origId) || ev;
    setModal({
      ...orig,
      start: orig.start instanceof Date ? orig.start.toISOString() : orig.start,
      end:   orig.end   instanceof Date ? orig.end.toISOString()   : orig.end
    });
  };

  const handleDelete = async (id) => {
    if (window.confirm('Eliminare l\'evento? Verranno eliminate anche tutte le occorrenze ricorrenti.')) {
      await deleteDoc(doc(db, 'crm_eventi', id));
      setModal(null);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Calendario</Typography>
          <Typography variant="body2" color="text.secondary">Demo, appuntamenti, riunioni.</Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Stack direction="row" spacing={0.5}>
            {Object.entries(OWNER_COLORS).map(([name, color]) => (
              <Chip key={name} size="small" label={name} sx={{ bgcolor: color, color: 'white' }} />
            ))}
          </Stack>
          <Button
            variant="contained" startIcon={<Add />}
            onClick={() => {
              const now = new Date();
              const inOneHour = new Date(now.getTime() + 60 * 60 * 1000);
              setModal({
                titolo: '', start: now.toISOString(), end: inOneHour.toISOString(),
                allDay: false, contattoId: null, luogo: '', note: '',
                visibility: 'public', ricorrenza: null
              });
            }}
          >
            Nuovo evento
          </Button>
        </Stack>
      </Box>

      <Paper sx={{ p: { xs: 1, md: 2 } }}>
        <Box sx={{ mb: 1, display: 'flex', justifyContent: 'flex-end' }}>
          <ToggleButtonGroup size="small" value={view} exclusive onChange={(e, v) => v && setView(v)}>
            <ToggleButton value="month">Mese</ToggleButton>
            <ToggleButton value="week">Settimana</ToggleButton>
            <ToggleButton value="day">Giorno</ToggleButton>
            <ToggleButton value="agenda">Agenda</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Box sx={{ height: { xs: 'calc(100vh - 260px)', md: 'calc(100vh - 220px)' }, minHeight: 500 }}>
          <Calendar
            localizer={localizer}
            culture="it"
            messages={messages}
            events={calendarEvents}
            startAccessor="start"
            endAccessor="end"
            view={view}
            onView={setView}
            date={date}
            onNavigate={setDate}
            selectable
            onSelectSlot={handleSelectSlot}
            onSelectEvent={handleSelectEvent}
            eventPropGetter={eventStyleGetter}
            popup
            style={{ height: '100%' }}
          />
        </Box>
      </Paper>

      {modal && (
        <EventModal
          initial={modal}
          contatti={contatti}
          staff={staff}
          isAdmin={isAdmin}
          onClose={() => setModal(null)}
          onDelete={handleDelete}
        />
      )}
    </Box>
  );
}